import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  readAdminCustomerRouteParam,
  readRouteParam,
} from '../../../_utils/route-param.utils';
import { TranslateModule } from '@ngx-translate/core';
import IPatient from '../../../_interfaces/IPatients';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import { PatientsService } from '../../../_services/patients/patients.service';
import { AdminStatePanelComponent } from '../../../components/admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminFormLayoutComponent } from '../../../components/admin-ui/admin-form-layout/admin-form-layout.component';
import { AdminPageHeaderComponent } from '../../../components/admin-ui/admin-page-header/admin-page-header.component';
import { FormPatientComponent } from '../../../components/forms/form-patient/form-patient.component';
import { FormPatientMetadataComponent } from '../../../components/forms/form-patient-metadata/form-patient-metadata.component';
import { FormPatientSitesComponent } from '../../../components/forms/form-patient-sites/form-patient-sites.component';
import { TablePatientsAppointmentsComponent } from '../../../components/tables/table-patients-appointments/table-patients-appointments.component';
import { TablePatientTreatmentsComponent } from '../../../components/tables/table-patients-treatments/table-patients-treatments.component';

@Component({
  selector: 'app-admin-patient-manage',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    AdminStatePanelComponent,
    AdminFormLayoutComponent,
    AdminPageHeaderComponent,
    FormPatientComponent,
    FormPatientMetadataComponent,
    FormPatientSitesComponent,
    TablePatientsAppointmentsComponent,
    TablePatientTreatmentsComponent,
  ],
  templateUrl: './admin-patient-manage.component.html',
  styleUrl: './admin-patient-manage.component.scss',
})
export class AdminPatientManageComponent implements OnInit {
  public customersId = 0;
  public patientsId = 0;
  public patient: IPatient | null = null;
  public patientTreatments: IPatientHasTreatment[] = [];
  public loading = true;
  public hasError = false;
  public listQueryParams: Record<string, string> = {};
  public activeTab:
    | 'profile'
    | 'metadata'
    | 'history'
    | 'sites'
    | 'appointments'
    | 'treatments' =
    'profile';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly patientsService: PatientsService,
  ) {}

  get baseRoute(): string {
    const customerRouteParam =
      readAdminCustomerRouteParam(this.route, this.customersId) || this.customersId;
    return `/administration/${customerRouteParam}/patients/${this.patientsId}`;
  }

  get backLink(): any[] {
    return [
      '/administration',
      readAdminCustomerRouteParam(this.route, this.customersId) || this.customersId,
      'patients',
    ];
  }

  private readListQueryParams(): Record<string, string> {
    const allowedParams = ['q', 'mainSite', 'page', 'rows'];
    const params: Record<string, string> = {};

    allowedParams.forEach((key) => {
      const value = this.route.snapshot.queryParamMap.get(key);
      if (value) {
        params[key] = value;
      }
    });

    return params;
  }

  ngOnInit(): void {
    this.customersId = readRouteParam(this.route, 'customers_id');
    this.patientsId = readRouteParam(this.route, 'patients_id');
    this.listQueryParams = this.readListQueryParams();
    const routeTab = this.route.snapshot.data['tab'] as string | undefined;
    this.activeTab = this.isValidTab(routeTab) ? routeTab : 'profile';

    this.route.data.subscribe((data) => {
      const tab = data['tab'] as string | undefined;
      this.activeTab = this.isValidTab(tab) ? tab : 'profile';
    });

    this.load();
  }

  private isValidTab(tab: string | null): tab is typeof this.activeTab {
    return (
      tab === 'profile' ||
      tab === 'metadata' ||
      tab === 'history' ||
      tab === 'sites' ||
      tab === 'appointments' ||
      tab === 'treatments'
    );
  }

  load(): void {
    if (!this.customersId || !this.patientsId) {
      this.hasError = true;
      this.loading = false;
      return;
    }

    this.loading = true;
    this.hasError = false;

    const resolvedPatient = (this.route.snapshot.data['patient'] || null) as
      | IPatient
      | null;

    const loadTreatments = (): void => {
      this.patientsService
        .getTreatments(this.customersId, this.patientsId)
        .subscribe({
          next: (treatments) => {
            this.patientTreatments = treatments;
            this.loading = false;
          },
          error: () => {
            this.patientTreatments = [];
            this.loading = false;
          },
        });
    };

    if (resolvedPatient) {
      this.patient = resolvedPatient;
      loadTreatments();
      return;
    }

    this.patientsService.get(this.customersId, this.patientsId).subscribe({
      next: (patient: IPatient) => {
        this.patient = patient;
        loadTreatments();
      },
      error: () => {
        this.hasError = true;
        this.loading = false;
      },
    });
  }
}
